// Tenantra Inc. - Slide-in property summary opened from the Properties table
import { useState } from 'react';
import { ArrowRight, FileText, Plus } from 'lucide-react';
import { navigate } from '@/services/navigation';
import { DetailSheet, Field, Section, StatusText, PrimaryAction, SecondaryAction, fmtDate } from './DetailPanel';
import { PropertyUnits } from './PropertyUnits';
import { ScreeningLinkSection } from './ScreeningLinkSection';
import { EntityNotes } from './EntityNotes';
import { StatementDialog } from './StatementDialog';

const VERIFY = {
  verified: { label: 'Verified', cls: 'text-emerald-600', dot: 'bg-emerald-500' },
  rejected: { label: 'Rejected', cls: 'text-red-600', dot: 'bg-red-500' },
  needs_documents: { label: 'Needs documents', cls: 'text-amber-600', dot: 'bg-amber-500' },
};
const PENDING = { label: 'Pending verification', cls: 'text-amber-600', dot: 'bg-amber-500' };

export function PropertyPanel({ property, typeLabel, onClose, onUnitsChange }) {
  const a = property?.address;
  const verified = property?.verification_status === 'verified';
  const [statement, setStatement] = useState(false);

  return (
    <DetailSheet
      open={!!property} onClose={onClose} loading={false} testId="property-panel"
      crumb="Properties / Property" title={a && `${a.unit_number && (property.units?.length || 1) <= 1 ? `Unit ${a.unit_number}, ` : ''}${a.street}`}
      meta={property && <><span className="text-slate-500">{a.city}, {a.province}</span><StatusText cfg={VERIFY[property.verification_status] || PENDING} /></>}
      footer={property && (verified
        ? <>
            <PrimaryAction onClick={() => navigate(`/landlord/tenants/new?property=${property.id}`)} testId="panel-add-tenancy"><Plus className="w-4 h-4" /> Add tenancy</PrimaryAction>
            <SecondaryAction onClick={() => navigate(`/landlord/properties/${property.id}/verify`)} testId="panel-view-verification">View verification</SecondaryAction>
          </>
        : <PrimaryAction onClick={() => navigate(`/landlord/properties/${property.id}/verify`)} testId="panel-verify-property">Complete verification <ArrowRight className="w-4 h-4" /></PrimaryAction>)}
    >
      {property && <>
        <Section title="Property">
          <dl className="grid grid-cols-2 gap-4">
            <Field label="Address" wide>{a.unit_number ? `Unit ${a.unit_number}, ` : ''}{a.street}, {a.city}, {a.province} {a.postal_code}</Field>
            <Field label="Type">{typeLabel}{property.units?.length > 1 ? ` · ${property.units.length} units` : ''}</Field>
            <Field label="Added">{fmtDate(property.created_at)}</Field>
          </dl>
        </Section>
        <Section title="Verification">
          <p className="text-sm text-slate-800" data-testid="panel-verification">
            {verified ? <>Ownership verified on {fmtDate(property.verified_at)}</>
              : property.rejection_reason ? <span className="text-red-600">{property.rejection_reason}</span>
              : 'Verification is not complete. Reports can be filed once ownership is verified.'}
          </p>
        </Section>
        <PropertyUnits propertyId={property.id} onUnitsChange={onUnitsChange} />
        <ScreeningLinkSection property={property} />
        <Section title="Statements">
          <div className="flex items-center justify-between gap-4">
            <p className="text-sm text-slate-600">Rent, other income and outstanding amounts for this property as a PDF, by month, quarter, year or custom dates.</p>
            <button onClick={() => setStatement(true)} className="flex-shrink-0 text-xs font-medium text-teal-700 hover:text-teal-800 inline-flex items-center gap-1" data-testid="panel-statement-btn"><FileText className="w-3.5 h-3.5" /> Create statement</button>
          </div>
        </Section>
        <EntityNotes type="property" id={property.id} />
        <StatementDialog open={statement} onClose={() => setStatement(false)} propertyId={property.id} properties={[property]} />
      </>}
    </DetailSheet>
  );
}
